import { Injectable } from '@angular/core';
import { AbstractRequest } from '@shared/requests/AbstractRequest';
import Bond from '@shared/models/Bond';
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import BondCreate from '@shared/models/BondCreate';
import Count from '@shared/models/Count';
import { Observable, catchError, of, firstValueFrom } from 'rxjs';
import { IBondDeposit } from '@shared/models/BondDeposit';

@Injectable({
  providedIn: 'root',
})
export class BondService extends AbstractRequest {
  public fetchAll(
    page?: number,
    items?: number,
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
  ): Observable<Bond[]> {
    const names = ['page', 'items', 'state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate', 'id'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Bond[]>(`${this.endpoint}/bonds/owner`, {
        params,
      });
  }

  public count(
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string,
    sortDirection?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<Count> {
    const names = ['state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Count>(`${this.endpoint}/bonds/owner/count`, {
        params,
      });
  }

  public userFetchAll(
    page?: number,
    items?: number,
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
  ): Observable<Bond[]> {
    const names = ['page', 'items', 'state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Bond[]>(`${this.endpoint}/bonds/user`, {
        params,
      });
  }

  public exportSelectionAll(
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
  ): Observable<Bond[]> {
    const names = ['state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatForStateArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Bond[]>(`${this.endpoint}/bonds/export/owner`, {
        params,
      });
  }

  public userCount(
    state: Array<string> | null = null,
    type?: string | null,
    sortBy?: string,
    sortDirection?: string,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<Count> {
    const names = ['state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Count>(`${this.endpoint}/bonds/user/count`, {
        params,
      });
  }

  public postBondDeposit(bond: IBondDeposit, link : string): Promise<Bond> {
    return firstValueFrom(this.http.post<Bond>(`${this.endpoint}/bond-deposit/${link}`, bond));
  }

  public create(bond: BondCreate, send: boolean): Observable<Bond> {
    return this.http.post<Bond>(`${this.endpoint}/bonds/${send}`, bond);
  }

  public update(bond: any | Bond, id: string): Observable<Bond> {
    return this.http.put<Bond>(`${this.endpoint}/bonds/${id}`, bond);
  }

  public updateForTransaction(bond: BondCreate | Bond, id: string): Observable<Bond> {
    return this.http.put<Bond>(`${this.endpoint}/bonds/${id}/transaction`, bond);
  }

  public finish(id: string | number): Observable<void> {
    return this.http.get<void>(`${this.endpoint}/bonds/${id}/finish`);
  }

  public qrcode(id: number): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/bonds/${id}/qrcode`);
  }

  public fetch(id: string, who = 'owner'): Observable<Bond> {
    return this.http.get<Bond>(`${this.endpoint}/bonds/${who}/${id}`);
  }

  public fetchAlreadyExistingBondByProperty(id: number): Observable<Bond> {
    return this.http.get<Bond>(`${this.endpoint}/bonds/property/${id}`);
  }

  public sendMailCheckInCheckOut(id: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpoint}/bonds/${id}/sendMailCheckInCheckOut`, {});
  }

  public fetchBondListByProperty(
    page: number,
    items: number,
    state: string | null = null,
    type: string | null,
    sortBy: string | null,
    sortDirection: string | null,
    search: string | null,
    startDate: string | null,
    endDate: string | null,
    id: number,
  ): Observable<Bond[]> {
    const names = ['page', 'items', 'state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Bond[]>(`${this.endpoint}/bonds/property/list/${id}`, {
        params,
      });
  }

  public countByProperty(
    state: string | null = null,
    type: string | null,
    sortBy: string,
    sortDirection: string,
    search: string,
    startDate: string,
    endDate: string,
    id: number,
  ): Observable<Count> {
    const names = ['state', 'type', 'sortBy', 'sortDirection', 'search', 'startDate', 'endDate'];
    const args = this.formatArgs(names, arguments);

    const params = new HttpParams({
      fromObject: args,
    });

    return this.http
      .get<Count>(`${this.endpoint}/bonds/property/${id}/count`, {
        params,
      });
  }

  public cancel(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/bonds/${id}`);
  }

  public retry(id: string): Observable<void> {
    return this.http.get<void>(`${this.endpoint}/bonds/${id}/retry`);
  }

  public read(id: string): Observable<Bond> {
    return this.http.get<Bond>(`${this.endpoint}/bonds/${id}`);
  }

  public zip(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(`${this.endpoint}/bonds/${id}/zip`, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  public fetchAdmin(id: string, who = 'owner'): Observable<Bond> {
    return this.http.get<Bond>(`${this.endpoint}/bonds/admin/${who}/${id}`);
  }

  public finishAdmin(id: string | number): Observable<void> {
    return this.http.get<void>(`${this.endpoint}/bonds/admin/${id}/finish`);
  }

  public cancelAdmin(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/bonds/admin/${id}`);
  }

  public retryAdmin(id: string): Observable<void> {
    return this.http.get<void>(`${this.endpoint}/bonds/admin/${id}/retry`);
  }

  public updateAdmin(bond: BondCreate | Bond, id: string): Observable<Bond> {
    return this.http.put<Bond>(`${this.endpoint}/bonds/admin/${id}`, bond);
  }
}
